// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { screenerApi } from '@shared/data-access/api/ai/screener-api';
import { screenerApi as rtkScreenerApi } from '@advisor-app/store/api/ai/screener-api';
import { generatePayloadCustomUniverse } from '@advisor-app/features/utils/generate-payload-custom-universe';
import { useHandleApiErrors } from '@advisor-app/hooks/use-handle-api-errors';
import { type Universe } from '@shared/data-access/interfaces/redux/parameters-state';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useEffect, useState } from 'react';
import {
  type Stock,
  type StocksSummaryResp,
} from '@shared/data-access/interfaces/api/ai/screener';

interface UseFetchUniverseDataResponse {
  summaryUniverse: StocksSummaryResp;
  universeData: Stock[];
}

const initialState = {
  dividend_yield: 0,
  market_cap: 0,
  number_stocks: 0,
  pb: 0,
  pe: 0,
};

export const useFetchUniverseData = (
  universe: Universe,
): UseFetchUniverseDataResponse => {
  const handleApiErrors = useHandleApiErrors();
  const runSafeUpdate = useSafeUpdate();
  const [summaryUniverse, setSummaryUniverse] =
    useState<StocksSummaryResp>(initialState);
  const [universeData, setUniverseData] = useState<Stock[]>([]);
  const [triggerGetStockSummary] = screenerApi.useLazyGetStockSummaryQuery();
  const [searchScreener] = rtkScreenerApi.useLazySearchScreenerQuery();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = generatePayloadCustomUniverse(universe);

        // Use RTK Query mutations
        const [summaryResult, universeResult] = await Promise.all([
          triggerGetStockSummary(payload).unwrap(),
          searchScreener({
            payload,
            assetType: 'stock',
          }).unwrap(),
        ]);

        const filteredUniverseData = (universeResult as unknown as Stock[]).filter(
          stock => stock.currency !== 'missing',
        );

        runSafeUpdate(() => {
          setSummaryUniverse(summaryResult);
          setUniverseData(filteredUniverseData);
        });
      } catch (error: any) {
        handleApiErrors(error);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    universe.esg_exclusions,
    universe.esg_not_informed,
    universe.esg_scoring,
    universe.factors_filter,
    universe.sectors_filter,
    universe.to_exclude,
    universe.to_include,
    universe.universe_base,
    runSafeUpdate,
    triggerGetStockSummary,
    searchScreener,
  ]);

  return { summaryUniverse, universeData };
};
